import { DropDown } from "@/components/Core/DropDown";
import { Input } from "@/components/Core/Input";
import ViewField from "@/components/ViewField";
import { useEffect, useMemo } from "react";
import styles from "./WarehousesByState.module.css";
import { cn } from "@/helper/HelperFunction";

const normalizeWarehouseId = (warehouseId) => {
  if (!warehouseId) return "";
  if (typeof warehouseId === "string") return warehouseId;
  return warehouseId?._id || "";
};

const normalizeStateId = (state) => {
  if (!state) return "";
  if (typeof state === "string") return state;
  return state?._id || "";
};

const normalizeWarehouseRows = (rows, warehouseOptions = []) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeOptions = Array.isArray(warehouseOptions) ? warehouseOptions : [];

  return safeRows
    .filter(Boolean)
    .map((row) => {
      const warehouseId = normalizeWarehouseId(row?.warehouseId);
      const matchedWarehouse = safeOptions.find((w) => w?._id === warehouseId);
      const rawQuantity = row?.quantityAvailable;
      const quantityAvailable =
        rawQuantity === "" || rawQuantity === null || rawQuantity === undefined
          ? 0
          : Number(rawQuantity);

      return {
        warehouseId,
        state:
          normalizeStateId(row?.state) ||
          normalizeStateId(matchedWarehouse?.state) ||
          "",
        quantityAvailable,
      };
    })
    .filter((row) => row?.warehouseId);
};

const buildRowsForSelectedStates = ({
  selectedStateIds = [],
  warehouseOptions = [],
  prevRows = [],
  showAllWarehouses = false,
}) => {
  const safeSelectedStateIds = Array.isArray(selectedStateIds)
    ? selectedStateIds
    : [];
  const safeOptions = Array.isArray(warehouseOptions) ? warehouseOptions : [];

  const normalizedPrev = normalizeWarehouseRows(prevRows, safeOptions);
  const prevByWarehouseId = new Map(
    normalizedPrev.map((row) => [row?.warehouseId, row])
  );

  // If showAllWarehouses is true, return all warehouses regardless of state
  const desiredWarehouses = showAllWarehouses
    ? safeOptions
    : safeOptions.filter((w) =>
        safeSelectedStateIds.includes(normalizeStateId(w?.state))
      );

  return desiredWarehouses
    .filter((w) => w?._id)
    .map((w) => {
      const existing = prevByWarehouseId.get(w?._id);
      return {
        warehouseId: w?._id || "",
        state: normalizeStateId(w?.state) || "",
        // Default to 0 when a state/warehouse is newly added.
        quantityAvailable: existing?.quantityAvailable ?? 0,
      };
    });
};

export default function WarehousesByState({
  allStates = [],
  selectedStateIds = [],
  setSelectedStateIds,
  warehouseOptions = [],
  warehouses,
  setWarehouses,
  isEdit = false,
}) {
  const safeSelectedStateIds = useMemo(
    () => (Array.isArray(selectedStateIds) ? selectedStateIds : []),
    [selectedStateIds]
  );

  // Check if "all" state (with isDefault === true) is selected
  const selectedAllState = allStates?.find(
    (state) =>
      safeSelectedStateIds?.includes(state?._id) && state?.isDefault === true
  );
  const isAllSelected = !!selectedAllState;

  // Determine if dropdown should be multi-select
  const isMulti = !isAllSelected;

  useEffect(() => {
    setWarehouses((prev) => {
      return buildRowsForSelectedStates({
        selectedStateIds: isAllSelected ? [] : safeSelectedStateIds,
        warehouseOptions,
        prevRows: prev,
        showAllWarehouses: isAllSelected,
      });
    });
  }, [safeSelectedStateIds, warehouseOptions, setWarehouses, isAllSelected]);

  const safeRows = normalizeWarehouseRows(warehouses, warehouseOptions);

  return (
    <div className={styles.warehousesByState}>
      <DropDown
        options={allStates}
        label="States"
        placeholder="Select States"
        value={
          isAllSelected
            ? selectedAllState
            : allStates?.filter((option) =>
                safeSelectedStateIds?.includes(option?._id)
              )
        }
        setter={(selectedOptions) => {
          // Normalize selectedOptions to array
          const selectedArray = Array.isArray(selectedOptions)
            ? selectedOptions
            : selectedOptions
            ? [selectedOptions]
            : [];

          // Check if "all" state (isDefault === true) is in the selection
          const allStateInSelection = selectedArray.find(
            (option) => option?.isDefault === true
          );

          if (allStateInSelection) {
            // If "all" is selected, only keep "all" state (single select mode)
            setSelectedStateIds([allStateInSelection?._id]);
          } else {
            // Otherwise, keep all selected states (multi-select mode)
            const newStateIds =
              selectedArray?.map((option) => option?._id) || [];
            setSelectedStateIds(newStateIds);
          }
        }}
        optionLabel={"name"}
        optionValue={"_id"}
        isMulti={isMulti}
      />

      <div className={cn(styles.fullGrid, styles.warehousesByStateWrapper)}>
        {isAllSelected ? (
          // Show all warehouses when "all" state (isDefault === true) is selected
          <div className={styles.stateWrapper}>
            <div className={styles.stateHeader}>
              <h3>
                {selectedAllState?.name || "All Warehouses"}
              </h3>
            </div>

            {safeRows?.length === 0 ? <p>No warehouses found.</p> : null}

            {safeRows?.map((row) => (
              <div key={row?.warehouseId} className={styles.warehouseRow}>
                <ViewField
                  label="Warehouse"
                  value={
                    warehouseOptions?.find(
                      (option) => option?._id === row?.warehouseId
                    )?.name
                  }
                  type="input"
                  inputClass={styles.warehouseInput}
                />
                <Input
                  label="Quantity"
                  placeholder="Enter Quantity"
                  value={row?.quantityAvailable}
                  setter={(val) => {
                    setWarehouses((prev) => {
                      const prevRows = buildRowsForSelectedStates({
                        selectedStateIds: [],
                        warehouseOptions,
                        prevRows: prev,
                        showAllWarehouses: true,
                      });
                      return prevRows.map((r) =>
                        r?.warehouseId === row?.warehouseId
                          ? {
                              ...r,
                              // Keep numeric in state; default to 0 if cleared.
                              quantityAvailable:
                                val === "" || val === null || val === undefined
                                  ? 0
                                  : Number(val),
                            }
                          : r
                      );
                    });
                  }}
                  regexType={"number"}
                />
              </div>
            ))}
          </div>
        ) : (
          // Show warehouses grouped by state when other states are selected
          safeSelectedStateIds?.map((stateId) => {
            const rowsForState = safeRows.filter(
              (row) => row?.state === stateId
            );

            return (
              <div key={stateId} className={styles.stateWrapper}>
                <div className={styles.stateHeader}>
                  <h3>
                    {allStates?.find((option) => option?._id === stateId)?.name}
                  </h3>
                </div>

                {rowsForState?.length === 0 ? (
                  <p>No warehouses found for this state.</p>
                ) : null}

                {rowsForState?.map((row) => (
                  <div key={row?.warehouseId} className={styles.warehouseRow}>
                    <ViewField
                      label="Warehouse"
                      value={
                        warehouseOptions?.find(
                          (option) => option?._id === row?.warehouseId
                        )?.name
                      }
                      type="input"
                      inputClass={styles.warehouseInput}
                    />
                    <Input
                      label="Quantity"
                      placeholder="Enter Quantity"
                      value={row?.quantityAvailable}
                      setter={(val) => {
                        setWarehouses((prev) => {
                          const prevRows = buildRowsForSelectedStates({
                            selectedStateIds: safeSelectedStateIds,
                            warehouseOptions,
                            prevRows: prev,
                            showAllWarehouses: false,
                          });
                          return prevRows.map((r) =>
                            r?.warehouseId === row?.warehouseId
                              ? {
                                  ...r,
                                  // Keep numeric in state; default to 0 if cleared.
                                  quantityAvailable:
                                    val === "" ||
                                    val === null ||
                                    val === undefined
                                      ? 0
                                      : Number(val),
                                }
                              : r
                          );
                        });
                      }}
                      regexType={"number"}
                    />
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
